/**
 * Public catalogue + checkout API.
 *
 * Types mirror the engine's response shapes exactly (see engine
 * src/routes/events.js and src/routes/orders.js). Where the engine returns
 * cents, the field name says so and the value stays an integer.
 */

import { api, apiRequest } from './apiClient';
import { currentAffiliateCode } from './attribution';
import type { PublicAdmission } from './admissions';

// ── Events ──────────────────────────────────────────────────────────────────

export interface EventCardData {
  id: string;
  title: string;
  featuredImageUrl: string | null;
  venueName: string | null;
  city: string | null;
  state: string | null;
  date: string | null;
  time: string | null;
  genre: string | null;
  category: string | null;
  fromPriceCents: number | null;
  ticketsAvailable: number;
  soldOut: boolean;
}

export interface TicketTier {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  remaining: number;
  status: string;
  saleStart: string | null;
  saleEnd: string | null;
}

export interface LineupArtist {
  artistId: string;
  stageName: string;
  genre: string | null;
  profileImageUrl: string | null;
  /** Null until the artist has 5+ performed events — see buildingTrackRecord. */
  reliabilityScore: number | null;
  buildingTrackRecord: boolean;
}

export interface EventDetail {
  id: string;
  title: string;
  description: string | null;
  featuredImageUrl: string | null;
  /** Optional hero video. The image above stays the fallback and is never replaced by it. */
  featuredVideoUrl: string | null;
  featuredVideoWebmUrl: string | null;
  venue: { name: string | null; address: string | null; city: string | null; state: string | null };
  date: string | null;
  time: string | null;
  doorsTime: string | null;
  ageRestriction: string | null;
  genre: string | null;
  category: string | null;
  status: string;
  complianceBadge: 'fully_permitted' | 'permit_pending';
  tiers: TicketTier[];
  lineup: LineupArtist[];
  /**
   * How this event admits people, resolved by the server.
   *
   * Optional: an older engine, or a response cached from before admissions shipped, simply
   * omits it — and the absence means `ticketed`, which is what every event did then and
   * what the surface falls back to.
   */
  admission?: PublicAdmission;
}

export interface EventFilters {
  city?: string;
  genre?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Cents, matched against each event's lowest tier price. */
  minPrice?: number;
  maxPrice?: number;
}

export function buildEventQuery(filters: EventFilters): string {
  const params = new URLSearchParams();
  if (filters.city?.trim()) params.set('city', filters.city.trim());
  if (filters.genre?.trim()) params.set('genre', filters.genre.trim());
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.minPrice != null) params.set('minPrice', String(filters.minPrice));
  if (filters.maxPrice != null) params.set('maxPrice', String(filters.maxPrice));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const listEvents = (filters: EventFilters = {}) =>
  api.get<EventCardData[]>(`/events${buildEventQuery(filters)}`);

export const getEvent = (eventId: string) => api.get<EventDetail>(`/events/${eventId}`);

export const getEventTiers = (eventId: string) =>
  api.get<TicketTier[]>(`/events/${eventId}/tiers`);

// ── Investment opportunity (public aggregate) ────────────────────────────────

/**
 * Two shapes, decided by the engine.
 *
 * Everyone gets the teaser. The financial fields are present ONLY when the caller
 * passed the double gate (authenticated + investor + approved_by_admin), and the
 * engine omits the keys entirely rather than nulling them — so `financialsVisible`
 * is the flag to branch on, and the optional fields are genuinely absent for
 * everyone else.
 */
export interface EventOpportunity {
  opportunityId: string;
  title: string;
  description: string | null;
  status: string;
  closesAt: string | null;
  financialsVisible: boolean;
  targetCents?: number;
  raisedCents?: number;
  percentFunded?: number;
  minimumInvestmentCents?: number;
  pricePerShareCents?: number;
}

/**
 * Returns null when the event has no open raise — the engine answers 204, which
 * the client surfaces as an empty body rather than an error.
 */
export async function getEventOpportunity(eventId: string): Promise<EventOpportunity | null> {
  const result = await apiRequest<EventOpportunity | null | ''>(
    `/events/${eventId}/opportunity`,
    { method: 'GET' },
  );
  return result && typeof result === 'object' ? result : null;
}

// ── Checkout ────────────────────────────────────────────────────────────────

export interface CheckoutBuyer {
  firstName: string;
  lastName: string;
  email: string;
}

export interface CheckoutItem {
  tierId: string;
  quantity: number;
}

export interface CreatedOrder {
  orderId: string;
  /** Server-computed. Never derived client-side — see OrderSummary. */
  totalCents: number;
  /**
   * 'pending' when payment is still owed, 'complete' when the order was fulfilled
   * outright. A $0 order is settled by definition and comes back already complete.
   */
  status: 'pending' | 'complete';
  /** False only for a genuinely zero-total order. Branch on this, not on clientSecret. */
  paymentRequired: boolean;
  /** ABSENT for a $0 order: there is no PaymentIntent, so there is nothing to confirm. */
  clientSecret?: string;
  /** Present only when the order was fulfilled outright. */
  ticketsIssued?: number;
  /** Opaque per-order token; the only way a guest can read their own order. */
  accessToken: string;
}

export const createOrder = (input: {
  eventId: string;
  buyer: CheckoutBuyer;
  items: CheckoutItem[];
}) => api.post<CreatedOrder>('/orders', {
  ...input,
  // Affiliate attribution, fallback path. The engine prefers its own httpOnly cookie and
  // only falls back to this when the cookie could not be set, so sending null here is the
  // ordinary case and costs nothing. See lib/attribution.ts.
  affiliateCode: currentAffiliateCode(),
});

// ── Order status / history ──────────────────────────────────────────────────

export interface OrderTicket {
  ticketId: string;
  ticketUid: string;
  tierName: string | null;
  priceCents: number | null;
  status: string;
  /** Present only once the payment webhook has activated the ticket. */
  qr: string | null;
}

export interface OrderStatus {
  orderId: string;
  status: 'pending' | 'complete' | 'refunded' | 'partially_refunded' | 'expired';
  totalCents: number;
  createdAt: string;
  paymentConfirmedAt: string | null;
  buyerFirstName: string | null;
  /** Full name, for printing on the ticket. */
  buyerName?: string | null;
  buyerEmail: string | null;
  /** Short, human-quotable form of the order id. Derived server-side, never stored. */
  orderReference?: string | null;
  event: {
    eventId: string;
    title: string;
    date: string | null;
    time: string | null;
    doorsTime?: string | null;
    ageRestriction?: string | null;
    /** Null when the organizer set none; the ticket renders its own fallback. */
    imageUrl?: string | null;
    venueName?: string | null;
    venueAddress?: string | null;
    venue: string;
  };
  tickets: OrderTicket[];
}

export interface ResendResult {
  queued: boolean;
  /** Masked — enough to identify the mailbox, not enough to read over a shoulder. */
  sentTo: string;
  /** The rotated order token. Store it: the previous one stops working. */
  accessToken: string;
}

/**
 * Read one order. A guest passes the token from checkout; a signed-in buyer who
 * owns the order needs none. The token travels in a header rather than the query
 * string so it stays out of browser history and any access log.
 */
export const getOrderStatus = (orderId: string, accessToken?: string | null) =>
  api.get<OrderStatus>(
    `/orders/${orderId}/status`,
    accessToken ? { headers: { 'x-order-token': accessToken } } : undefined,
  );

export const listMyOrders = () => api.get<OrderStatus[]>('/orders/mine');

/**
 * Ask the engine to send the confirmation email again.
 *
 * The destination is never sent: the engine always uses the address already on the
 * order. A successful resend ROTATES the order token, so the caller must store the one
 * that comes back or its next request will 404.
 */
export const resendOrderConfirmation = (orderId: string, accessToken?: string | null) =>
  api.post<ResendResult>(
    `/orders/${orderId}/resend`,
    {},
    accessToken ? { headers: { 'x-order-token': accessToken } } : undefined,
  );
